import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  DestroyRef,
  afterNextRender,
  computed,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgClass } from '@angular/common';
import { LanguageToggleComponent } from '../language-toggle/language-toggle.component';
import { TranslateService } from '../../../core/services/translate.service';
import { ThemeService } from '../../../core/services/theme.service';
import { MagneticDirective } from '../../directives/magnetic.directive';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LanguageToggleComponent, MagneticDirective, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav
      class="fixed top-0 z-50 w-full border-b border-main transition-all duration-300"
      [ngClass]="scrolled() ? 'bg-[var(--color-navbar-bg)] backdrop-blur-xl' : 'bg-transparent'"
    >
      <div class="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <a
          [routerLink]="['/', lang()]"
          class="text-lg font-bold text-main"
          [style.view-transition-name]="'nav-logo'"
        >
          AJC<span style="color: var(--color-secondary);">.</span>
        </a>

        <!-- Desktop nav -->
        <div class="hidden items-center gap-4 lg:flex">
          <a
            [routerLink]="['/', lang()]"
            fragment="about"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.about') }}</a
          >
          <a
            [routerLink]="['/', lang()]"
            fragment="experience"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.experience') }}</a
          >
          <a
            [routerLink]="['/', lang()]"
            fragment="tech-stack"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.stack') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'projects']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.projects') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'lab']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.lab') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'showcase']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.showcase') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'now']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.now') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'guestbook']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.guestbook') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'battle']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.battle') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'services']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.services') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'contact']"
            routerLinkActive="text-main"
            class="whitespace-nowrap text-sm text-subtle transition-colors hover:text-main"
            >{{ t.t('nav.contact') }}</a
          >
          <a
            [routerLink]="['/', lang(), 'cv']"
            appMagnetic
            [strength]="0.18"
            class="rounded-lg px-4 py-2 text-sm font-medium text-main transition-all"
            style="background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));"
          >
            {{ t.t('nav.cv') }}
          </a>

          <!-- LinkedIn icon -->
          <a
            href="https://www.linkedin.com/in/adrianjimenezcabello"
            target="_blank"
            rel="noopener noreferrer"
            class="flex h-8 w-8 items-center justify-center rounded-lg text-dimmed transition-colors hover:text-[var(--color-info)]"
            aria-label="LinkedIn"
          >
            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
          </a>

          <!-- Theme toggle -->
          <button
            type="button"
            (click)="theme.toggle()"
            [attr.title]="
              theme.theme() === 'dark' ? t.t('nav.tooltip_light') : t.t('nav.tooltip_dark')
            "
            [attr.aria-label]="
              theme.theme() === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
            "
            class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-subtle transition-colors hover:bg-surface-strong hover:text-main"
          >
            @if (theme.theme() === 'light') {
              <!-- Sun icon for Light Mode -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-5 w-5"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2" />
                <path d="M12 20v2" />
                <path d="m4.93 4.93 1.41 1.41" />
                <path d="m17.66 17.66 1.41 1.41" />
                <path d="M2 12h2" />
                <path d="M20 12h2" />
                <path d="m6.34 17.66-1.41 1.41" />
                <path d="m19.07 4.93-1.41 1.41" />
              </svg>
            } @else {
              <!-- Moon icon for Dark Mode -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-5 w-5"
                aria-hidden="true"
              >
                <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
              </svg>
            }
          </button>

          <app-language-toggle />
        </div>

        <!-- Mobile hamburger -->
        <button
          type="button"
          class="lg:hidden text-main"
          [attr.aria-label]="mobileOpen() ? t.t('nav.aria_close_menu') : t.t('nav.aria_open_menu')"
          [attr.aria-expanded]="mobileOpen()"
          (click)="mobileOpen.set(!mobileOpen())"
        >
          <svg
            class="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            @if (mobileOpen()) {
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            } @else {
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            }
          </svg>
        </button>
      </div>

      <!-- Mobile menu -->
      @if (mobileOpen()) {
        <div class="border-t border-main bg-[var(--color-bg)] backdrop-blur-xl px-6 py-4 lg:hidden">
          <div class="flex flex-col gap-4">
            <a
              [routerLink]="['/', lang()]"
              fragment="about"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.about') }}</a
            >
            <a
              [routerLink]="['/', lang()]"
              fragment="experience"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.experience') }}</a
            >
            <a
              [routerLink]="['/', lang()]"
              fragment="tech-stack"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.stack') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'projects']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.projects') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'lab']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.lab') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'showcase']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.showcase') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'now']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.now') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'guestbook']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.guestbook') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'battle']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.battle') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'services']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.services') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'contact']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.contact') }}</a
            >
            <a
              [routerLink]="['/', lang(), 'cv']"
              class="text-sm text-subtle"
              (click)="mobileOpen.set(false)"
              >{{ t.t('nav.cv') }}</a
            >
            <div class="flex items-center gap-3 pt-2 border-t border-main">
              <app-language-toggle />
              <a
                href="https://www.linkedin.com/in/adrianjimenezcabello"
                target="_blank"
                rel="noopener noreferrer"
                class="flex h-8 w-8 items-center justify-center rounded-lg text-dimmed transition-colors hover:text-[var(--color-info)]"
                aria-label="LinkedIn"
              >
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      }
    </nav>
  `,
})
export class NavbarComponent {
  readonly t = inject(TranslateService);
  readonly theme = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  readonly mobileOpen = signal(false);
  readonly scrolled = signal(false);
  readonly lang = computed(() => this.t.currentLang());

  constructor() {
    afterNextRender(() => {
      const onScroll = (): void => {
        this.scrolled.set(window.scrollY > 50);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      this.destroyRef.onDestroy(() => window.removeEventListener('scroll', onScroll));
    });
  }
}
